import {
  styled,
  Button,
  type ButtonProps,
  Menu,
  MenuItem,
  Icon,
  ListItemText,
  ListItemIcon,
  type MenuItemProps,
} from '@mui/material';
import isString from 'lodash/isString';
import { useState, type MouseEvent } from 'react';

import { type BaseComponentProps } from '../../utils/types';

export interface MenuButtonProps
  extends BaseComponentProps,
    Omit<ButtonProps, 'onClick'> {
  label?: React.ReactNode;
  value?: string;
  icon?: React.ReactNode;
  hideIndicator?: boolean;
  noPadding?: boolean;
  tooltip?: string;
  items: Array<
    {
      label: React.ReactNode;
      icon?: React.ReactNode;
      value: string;
      description?: React.ReactNode;
    } & MenuItemProps
  >;
  /**
   * Set the width of the menu instead of relying on menu items' widths
   */
  menuWidth?: number | string;
  handleChange?: (newValue: string) => void;
}

const StyledButton = styled(Button)(() => ({
  minWidth: 'unset',
}));

export const MenuButton = ({
  label,
  value,
  icon,
  items,
  menuWidth,
  handleChange,
  hideIndicator,
  noPadding,
  ...otherProps
}: MenuButtonProps) => {
  const { 'data-cy': dataCyValue, ...buttonProps } = otherProps;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleButton = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenu = () => {
    setAnchorEl(null);
  };

  const handleChoice = (newValue: string) =>
    handleChange && handleChange(newValue);

  return (
    <>
      <StyledButton
        {...buttonProps}
        data-cy={dataCyValue}
        startIcon={isString(icon) ? <Icon>{icon}</Icon> : icon}
        endIcon={!hideIndicator && <Icon>arrow_drop_down</Icon>}
        onClick={handleButton}
        sx={{
          padding: noPadding ? 0 : 'undefined',
        }}
      >
        {label}
      </StyledButton>
      <Menu
        data-cy={`${dataCyValue}-menu`}
        open={isOpen}
        anchorEl={anchorEl}
        onClick={handleMenu}
        onClose={handleMenu}
        slotProps={{ paper: { style: { width: menuWidth } } }}
      >
        {items.map(item => {
          const {
            value: itemValue,
            label: itemLabel,
            icon: itemIcon,
            description: itemDescription,
            ...itemProps
          } = item;
          return (
            <MenuItem
              data-cy={`${dataCyValue}-menuItem-${itemValue}`}
              key={itemValue}
              selected={itemValue === value}
              onClick={() => {
                handleChoice(itemValue);
                handleMenu();
              }}
              {...itemProps}
            >
              {itemIcon && (
                <ListItemIcon>
                  {isString(itemIcon) ? <Icon>{itemIcon}</Icon> : itemIcon}
                </ListItemIcon>
              )}
              <ListItemText
                primaryTypographyProps={{
                  // bold the title only if there's a description
                  fontWeight: itemDescription ? 'bold' : undefined,
                }}
                secondary={itemDescription}
                secondaryTypographyProps={{
                  sx: { textWrap: 'wrap' },
                  color: 'primary',
                }}
              >
                {itemLabel}
              </ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
